import React, { useState, useEffect } from 'react';
import SubNav from "../components/subNav";
import SEO from '../components/SEO';
import TaskColumn from '../components/taskColumn';
import { jsPDF } from 'jspdf';
import { baseUrl } from '../config';



function ProgressTracker() {
  const [tasks, setTasks] = useState([]);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [projectInfo, setProjectInfo] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportPreviewData, setReportPreviewData] = useState(null);
  const columns = ['New Task', 'Ready', 'In Progress', 'Ready for review', 'Done'];

  const fallbackTasks = [
    { id: 1, title: 'Fallback Task 1', column: 'New Task' },
    { id: 2, title: 'Fallback Task 2', column: 'In Progress' },
    { id: 3, title: 'Fallback Task 3', column: 'Done' },
  ];

  useEffect(() => {
    fetchTasks();
    fetchProjectInfo();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch('/api/v1/tasks');
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setTasks(data);
      setIsUsingFallback(false);
    } catch (error) {
      setTasks(fallbackTasks);
      setIsUsingFallback(true);
    }
  };

  const fetchProjectInfo = async () => {
    try {
      const response = await fetch('/api/v1/project-info');
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setProjectInfo(data);
    } catch (error) {
    }
  };

  const addTask = async (newTask) => {
    if (isUsingFallback) {
      setTasks((prevTasks) => [...prevTasks, { ...newTask, id: Date.now() }]);
      return;
    }

    try {
      const response = await fetch('/api/v1/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTask),
      });

      if (!response.ok) throw new Error('Error adding task');
      const createdTask = await response.json();
      setTasks((prevTasks) => [...prevTasks, createdTask]);
    } catch (error) {
      setTasks((prevTasks) => [...prevTasks, { ...newTask, id: Date.now() }]);
      setIsUsingFallback(true);
    }
  };

  const updateTask = async (index, updatedTask) => {
    if (isUsingFallback) {
      setTasks((prevTasks) => {
        const updatedTasks = [...prevTasks];
        updatedTasks[index] = updatedTask;
        return updatedTasks;
      });
      return;
    }

    try {
      const response = await fetch(`/api/v1/tasks/${updatedTask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTask),
      });

      if (!response.ok) throw new Error('Error updating task');
      const taskIndex = tasks.findIndex(task => task.id === updatedTask.id);
      const updatedTasks = [...tasks];
      updatedTasks[taskIndex] = updatedTask;
      setTasks(updatedTasks);
    } catch (error) {
      setTasks((prevTasks) => {
        const updatedTasks = [...prevTasks];
        updatedTasks[index] = updatedTask;
        return updatedTasks;
      });
      setIsUsingFallback(true);
    }
  };

  const handleDropTask = async (taskIndex, fromColumn, toColumn) => {
    const taskToMove = tasks[taskIndex];
    if (!taskToMove) return;

    const updatedTask = { ...taskToMove, column: toColumn };

    if (isUsingFallback) {
      setTasks((prevTasks) =>
        prevTasks.map((task, index) =>
          index === taskIndex ? updatedTask : task
        )
      );
      return;
    }

    try {
      const response = await fetch(`/api/v1/tasks/${taskToMove.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTask),
      });

      if (!response.ok) throw new Error('Error moving task');
      setTasks((prevTasks) =>
        prevTasks.map((task, index) =>
          index === taskIndex ? updatedTask : task
        )
      );
    } catch (error) {
      setTasks((prevTasks) =>
        prevTasks.map((task, index) =>
          index === taskIndex ? updatedTask : task
        )
      );
      setIsUsingFallback(true);
    }
  };

  const prepareReportData = () => {
    if (!projectInfo) {
      alert('Unable to generate report: Project information not available');
      return null;
    }

    return {
      projectName: projectInfo.name,
      projectId: projectInfo.id,
      date: new Date().toLocaleDateString(),
      tasksByStatus: columns.map(column => ({
        status: column,
        tasks: tasks.filter(task => task.column === column)
      }))
    };
  };

  const generateReport = async (format) => {
    const reportData = prepareReportData();
    if (!reportData) return;

    if (format === 'pdf') {
      generatePdfReport(reportData);
    } else if (format === 'doc') {
      generateDocReport(reportData);
    }
  };

  const generatePdfReport = (reportData) => {
    const doc = new jsPDF();
    let yPos = 20;

    // Header
    doc.setFontSize(20);
    doc.text('Progress Report', 105, yPos, { align: 'center' });
    yPos += 20;

    // Project Info
    doc.setFontSize(12);
    doc.text(`Project: ${reportData.projectName}`, 20, yPos);
    yPos += 10;
    doc.text(`Project ID: ${reportData.projectId}`, 20, yPos);
    yPos += 10;
    doc.text(`Date: ${reportData.date}`, 20, yPos);
    yPos += 20;

    // Tasks by Status
    reportData.tasksByStatus.forEach(statusGroup => {
      doc.setFontSize(14);
      doc.text(`${statusGroup.status} (${statusGroup.tasks.length} tasks)`, 20, yPos);
      yPos += 10;

      statusGroup.tasks.forEach(task => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(12);
        doc.text(`• ${task.title}`, 30, yPos);
        yPos += 10;
      });

      yPos += 10;
    });

    doc.save(`project_report_${reportData.projectId}.pdf`);
  };

  const generateDocReport = (reportData) => {
    let docContent = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; }
            h1 { text-align: center; }
            .project-info { margin: 20px 0; }
            .status-group { margin: 20px 0; }
            .task-list { margin-left: 20px; }
          </style>
        </head>
        <body>
          <h1>Progress Report</h1>
          <div class="project-info">
            <p><strong>Project:</strong> ${reportData.projectName}</p>
            <p><strong>Project ID:</strong> ${reportData.projectId}</p>
            <p><strong>Date:</strong> ${reportData.date}</p>
          </div>
    `;

    reportData.tasksByStatus.forEach(statusGroup => {
      docContent += `
        <div class="status-group">
          <h2>${statusGroup.status} (${statusGroup.tasks.length} tasks)</h2>
          <div class="task-list">
            ${statusGroup.tasks.map(task => `<p>• ${task.title}</p>`).join('')}
          </div>
        </div>
      `;
    });

    docContent += `
        </body>
      </html>
    `;

    const blob = new Blob([docContent], { type: 'application/msword' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `project_report_${reportData.projectId}.doc`;
    link.click();
  };

  const ReportModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-w-4xl max-h-[80vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Generate Report</h3>
        
        {/* Report Preview */}
        <div className="mb-6 p-4 border rounded bg-gray-50">
          <h4 className="text-lg font-semibold mb-4">Preview</h4>
          <div className="space-y-4">
            <div className="text-center text-2xl font-bold">Progress Report</div>
            <div className="space-y-2">
              <p><strong>Project:</strong> {projectInfo?.name}</p>
              <p><strong>Project ID:</strong> {projectInfo?.id}</p>
              <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
            </div>
            {columns.map(column => (
              <div key={column} className="mt-4">
                <h5 className="font-semibold">{column} ({tasks.filter(task => task.column === column).length} tasks)</h5>
                <ul className="ml-6 list-disc">
                  {tasks
                    .filter(task => task.column === column)
                    .map(task => (
                      <li key={task.id}>{task.title}</li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-around">
          <button
            onClick={() => {
              generateReport('pdf');
              setShowReportModal(false);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Download PDF
          </button>
          <button
            onClick={() => {
              generateReport('doc');
              setShowReportModal(false);
            }}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Download DOC
          </button>
        </div>
        <button
          onClick={() => setShowReportModal(false)}
          className="mt-4 w-full text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title="Production Progress Tracker"
        description="Track your film production's progress across all stages — pre-production, production, and post — with ProducerBuddy's progress tracker."
        url="https://producerbuddy.pilomation.com/progress-tracker"
        robots="noindex, nofollow"
      />
      <SubNav />
      <div className="relative min-h-screen">
        {/* Updated header section with proper spacing */}
        <div className="flex justify-between items-center p-4">
          <div className="flex-1"> {/* Empty div for spacing */}</div>
          <div className="flex flex-col items-center flex-1">
            <h2 className="text-2xl font-bold">Progress Tracker</h2>
            <p className="mt-3 font-semibold">"Keeping tasks on track, one step at a time."</p>
            {isUsingFallback && <p className="text-red-500">⚠️ Offline Mode: your data is not secure</p>}
          </div>
          <div className="flex-1 flex justify-end">
            <button
              onClick={() => setShowReportModal(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Generate Report
            </button>
          </div>
        </div>
        
        <div className="flex p-4 justify-around h-screen overflow-auto">
          {columns.map((column) => (
            <TaskColumn
              key={column}
              column={column}
              tasks={tasks.filter((task) => task.column === column)}
              onAddTask={addTask}
              onUpdateTask={(index, updatedTask) => updateTask(
                tasks.findIndex(t => t.id === updatedTask.id), 
                updatedTask
              )}
              onDropTask={handleDropTask}
            />
          ))}
        </div>
        {showReportModal && <ReportModal />}
      </div>
    </>
  );
}

export default ProgressTracker;
